/** Time gained or lost against a reference lap, by distance. */

import { required } from './columns';
import { axisGrid, interp } from './interp';
import { DEFAULT_STEP_M } from './resample';
import { roundTo } from './round';
import type { DeltaSeries, Samples } from './types';

/** Live gap vs a reference lap at the same distance (ms; positive = slower).
 *
 * null when the reference is empty or distM runs past its final sample —
 * edge clamping would otherwise inflate the gap at 1 ms per ms once the
 * reference lap "finishes".
 */
export function timeDeltaAt(distM: number, tS: number, ref: Samples): number | null {
  const dist = required(ref, 'dist');
  if (dist.length === 0 || distM > dist[dist.length - 1]!) return null;
  return (tS - interp(dist, required(ref, 't'), distM)) * 1000;
}

/** Time gained/lost vs the reference lap over distance (ms; positive = slower). */
export function timeDeltaSeries(
  lap: Samples,
  reference: Samples,
  step: number = DEFAULT_STEP_M,
): DeltaSeries {
  const lapDist = required(lap, 'dist');
  const refDist = required(reference, 'dist');
  if (lapDist.length === 0 || refDist.length === 0) return { dist: [], delta_ms: [] };
  const grid = axisGrid(Math.min(lapDist[lapDist.length - 1]!, refDist[refDist.length - 1]!), step);
  const lapT = required(lap, 't');
  const refT = required(reference, 't');
  const deltas = new Array<number>(grid.length);
  for (let i = 0; i < grid.length; i++) {
    const d = grid[i]!;
    deltas[i] = roundTo((interp(lapDist, lapT, d) - interp(refDist, refT, d)) * 1000, 1);
  }
  return { dist: grid, delta_ms: deltas };
}
